import { useEffect, useEffectEvent } from "react";
import { useSearchParams } from "react-router";
import { useDebounce } from "use-debounce";
import { useShallow } from "zustand/react/shallow";
import { useIntersectionObserver } from "usehooks-ts";
import { SpinnerLegacy } from "../../../../components";
import { InfiniteList } from "../../../../components/infiniteLoading/InfiniteList";
import { useStockList } from "../../../../hooks/api/v2/stockList/useStockList";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { abvFilterType } from "../../../discovery/v2/DiscoveryV2SearchStore";
import { updateCompleteQueryList } from "./lib/updateCompleteQueryList";
import { useStorefrontFilterStore } from "../../StorefrontFilterStore";
import { useStockListContext } from "../../useStockListContext";
import CategoryListElementContainer from "../CategoryListElement/CategoryListElementContainer";

export const StorefrontParentProductList = ({
  StorefrontTagId,
  currentIndex,
  completedQueryList,
  setCompletedQueryList,
  supplierId,
}: {
  StorefrontTagId?: number;
  currentIndex?: number;
  completedQueryList?: boolean[];
  setCompletedQueryList?: (list: boolean[]) => void;
  supplierId: number;
}) => {
  // Keep cached products rendered, but only fetch categories near the viewport.
  const [ref, isNearViewport] = useIntersectionObserver({
    rootMargin: "600px 0px",
  });
  const { business } = useSellarCurrentBusiness();
  const {
    selectedTags,
    selectedPriceTier,
    selectedAvailabilityTier,
    searchValue,
    selectedAbvTypes,
    selectedDietaryTypes,
    showOutOfStockProducts,
  } = useStorefrontFilterStore(
    useShallow((state) => ({
      selectedTags: state.selectedTags,
      selectedPriceTier: state.selectedPriceTier,
      selectedAvailabilityTier: state.selectedAvailabilityTier,
      searchValue: state.searchValue,
      selectedAbvTypes: state.selectedAbvTypes,
      selectedDietaryTypes: state.selectedDietaryTypes,
      showOutOfStockProducts: state.showOutOfStockProducts,
    })),
  );

  const [debouncedSearchValue] = useDebounce(searchValue, 500);
  const { unAuthorisedUserPostcode, supplierStorefront } =
    useStockListContext();

  const [searchParams] = useSearchParams();

  const storefrontLink =
    searchParams.get("storefrontLink") || searchParams.get("priceTierLink");
  const orderLinkFlowToken = searchParams.get("orderFlowToken");

  const productsListQuery = useStockList(
    {
      ParentId: null,
      active: true,
      SupplierId: supplierId,
      search: debouncedSearchValue,
      types: selectedTags,
      StorefrontTagId: StorefrontTagId,
      limit: 10,
      ...(!!business
        ? {
            PriceTierId: selectedPriceTier,
            AvailabilityTierId: selectedAvailabilityTier,
          }
        : {
            PriceTierId: supplierStorefront?.PriceTier?.id,
            AvailabilityTierId: supplierStorefront?.AvailabilityTier?.id,
          }),
      isPreview: !!(business?.Supplier && business.Supplier.id === supplierId),
      postcode: unAuthorisedUserPostcode,
      orderLinkFlowToken: orderLinkFlowToken ?? undefined,
      storefrontLinkPassword: storefrontLink ?? undefined,
      excludeProductType: "badge",
      showOutOfStockProducts,
      query: [
        ...(selectedAbvTypes.length
          ? selectedAbvTypes.map((type) => abvFilterType[type].query)
          : []),
        ...(selectedDietaryTypes.length
          ? ([
              {
                type: "dietaryType",
                modifier: "IN",
                value: Array.from(selectedDietaryTypes.values()),
              },
            ] as const)
          : []),
      ],
    },
    { enabled: isNearViewport },
  );

  const updateCompletedQueries = useEffectEvent(() => {
    updateCompleteQueryList({
      currentIndex,
      completedQueryList,
      setCompletedQueryList,
      productsListQuery,
    });
  });

  useEffect(() => {
    updateCompletedQueries();
  }, [
    productsListQuery.hasNextPage,
    productsListQuery.isFetching,
    productsListQuery.isSuccess,
  ]);

  return (
    <div ref={ref}>
      {productsListQuery.isPending ? (
        <SpinnerLegacy />
      ) : (
        <>
          {productsListQuery.data?.pages[0]?.length ? (
            <>
              <InfiniteList
                query={productsListQuery}
                className={"flex flex-col gap-2"}
              >
                {productsListQuery.data.pages.map((page) =>
                  page.map((item, i) => (
                    <div key={i}>
                      <CategoryListElementContainer category={item} />
                    </div>
                  )),
                )}
              </InfiniteList>
            </>
          ) : (
            <>
              {currentIndex !== undefined && (
                <div className={"text-center"}>
                  <span>No Results Found</span>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
